import React from "react";
import { motion } from "framer-motion";
import { COMPANY } from "@/lib/api";
import { services } from "@/data/services";
import { MapPin, Phone, Mail, Clock, ClipboardList, PackageCheck, Truck, Route as RouteIcon, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const HOW_IMG_1 = "https://customer-assets.emergentagent.com/job_laronta-logistics/artifacts/garw3c05_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2003_48_01%20PM.png";
const HOW_IMG_2 = "https://customer-assets.emergentagent.com/job_laronta-logistics/artifacts/60xoylqm_ChatGPT%20Image%20Jul%2010%2C%202026%2C%2004_38_27%20PM.png";

const steps = [
  { icon: ClipboardList, title: "Book & Confirm", desc: "Send us the pickup, drop-off, and cargo details. We confirm your window, truck size, and driver within one business day." },
  { icon: PackageCheck, title: "Careful Load-Up", desc: "Our team wraps, palletizes, and secures your freight with load bars, e-track, and pads before it ever moves." },
  { icon: RouteIcon, title: "Live Dispatched Transit", desc: "Dispatch monitors your load in real time — proactive re-routing around weather, traffic, and delays." },
  { icon: Truck, title: "Delivered On Time", desc: "Signed proof of delivery on every drop. You get status updates and a confirmation the second we're done." },
];

export function ServicesPage() {
  return (
    <div data-testid="services-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Services</div>
        <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter">Full-service logistics.</h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Whether it's a single last-mile drop or a scheduled contract route,
          our trucking fleet handles it — safely, on time, and with clear communication.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-brand-bg hover:bg-white transition-colors overflow-hidden"
              data-testid={`services-page-card-${i}`}
            >
              <div className="overflow-hidden">
                <img
                  src={s.img}
                  alt={`${s.title} — Laronta Pro Logistics`}
                  className="w-full h-52 object-cover transition-transform duration-500 hover:scale-105"
                  loading="lazy"
                  data-testid={`services-page-card-image-${i}`}
                />
              </div>
              <div className="p-8 lg:p-10">
                <s.icon className="h-8 w-8 text-primary-brand" strokeWidth={1.5} />
                <h3 className="font-heading text-2xl font-bold mt-6">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 border border-slate-200 bg-white p-8 lg:p-12 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-black">Have a specific need?</h3>
            <p className="text-slate-600 mt-2 text-sm">Tell us the route and we'll build a plan around it.</p>
          </div>
          <Link
            to="/quote"
            className="bg-primary-brand hover:bg-[#E04F00] text-white px-8 py-4 font-bold uppercase tracking-wider text-sm"
            data-testid="services-quote-cta"
          >
            Request a Quote
          </Link>
        </div>

        {/* HOW WE WORK */}
        <section className="mt-24" data-testid="how-we-work-section">
          <div className="max-w-3xl">
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">How We Work</div>
            <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter text-slate-900">
              A look inside the operation.
            </h2>
            <p className="mt-4 text-slate-600">
              From the moment you book to the second we sign off on delivery, every step is
              handled by a real crew — professional, careful, and dialed in. Here's a peek at
              our trucks, our drivers, and the standards we run by every single day.
            </p>
          </div>

          {/* Image collage 1 */}
          <div className="mt-10 border border-slate-200 bg-white p-3 sm:p-4">
            <img
              src={HOW_IMG_1}
              alt="Laronta Pro Logistic — trucks, drivers, and warehouse operations"
              className="w-full h-auto"
              loading="lazy"
              data-testid="how-we-work-image-1"
            />
          </div>

          {/* Steps */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                data-testid={`how-step-${i}`}
                className="bg-white p-7 lg:p-8"
              >
                <div className="flex items-center justify-between">
                  <s.icon className="h-8 w-8 text-primary-brand" strokeWidth={1.6} />
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading text-lg font-bold mt-6 text-slate-900">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Standards + image collage 2 side-by-side */}
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="h-5 w-5 text-primary-brand" />
                <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand">Our Standards</div>
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight text-slate-900">
                Safe & secure. On time, every time.
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Every driver wears the Laronta Pro uniform, every truck runs a pre-trip
                inspection, and every load is secured before the doors close. That's not
                marketing — it's the checklist we actually follow.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Safe & secure handling of every parcel and pallet",
                  "On-time performance measured across every route",
                  "Professional team in uniform, background-checked",
                  "Nationwide reach through our vetted carrier network",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-slate-800">
                    <span className="h-1.5 w-6 bg-primary-brand mt-2 flex-none" />
                    <span className="text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2 border border-slate-200 bg-white p-3 sm:p-4">
              <img
                src={HOW_IMG_2}
                alt="Laronta Pro Logistic — safe delivery, on time, professional team"
                className="w-full h-auto"
                loading="lazy"
                data-testid="how-we-work-image-2"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export function ServiceAreaPage() {
  return (
    <div data-testid="service-area-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Service Area</div>
        <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter">All 50 states covered.</h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Headquartered in Salt Lake City, Utah, and running freight coast to coast.
          Wherever your shipment needs to go in the United States, we can get it there.
        </p>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {COMPANY.coverage.map((c) => (
            <div key={c} className="bg-brand-bg px-6 py-10 hover:bg-white transition-colors flex items-center gap-3">
              <MapPin className="h-4 w-4 text-primary-brand" />
              <span className="font-heading font-bold uppercase tracking-wide">{c}</span>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-slate-600">…serving every state in the U.S., from our home base in Salt Lake City.</p>
        </div>
      </div>
    </div>
  );
}

export function ContactPage() {
  return (
    <div data-testid="contact-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Contact</div>
        <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter">Let's move something.</h1>
        <p className="mt-4 text-slate-600 max-w-2xl">
          Reach out any time. For fastest response on urgent freight, give us a call.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          <ContactCard
            icon={Phone}
            label="Call us"
            value={COMPANY.phone}
            href={`tel:${COMPANY.phoneRaw}`}
            testid="contact-phone"
          />
          <ContactCard
            icon={Mail}
            label="Email"
            value={COMPANY.email}
            href={`mailto:${COMPANY.email}`}
            testid="contact-email"
          />
          <ContactCard
            icon={MapPin}
            label="Location"
            value={COMPANY.location}
            testid="contact-location"
          />
        </div>

        <div className="mt-10 border border-slate-200 bg-white p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="h-5 w-5 text-primary-brand" />
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-primary-brand">Business Hours</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COMPANY.hours.map(([d, t]) => (
              <div key={d} className="flex justify-between md:block border-b md:border-b-0 border-slate-200 pb-3">
                <div className="text-slate-500 text-xs uppercase tracking-[0.2em] font-bold">{d}</div>
                <div className="font-heading font-bold text-slate-900 mt-1">{t}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 border border-slate-200 bg-white p-8 lg:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-black">Prefer to send details first?</h3>
            <p className="text-slate-600 mt-2 text-sm">Fill out our quote form and we'll follow up within one business day.</p>
          </div>
          <Link
            to="/quote"
            className="bg-primary-brand hover:bg-[#E04F00] text-white px-8 py-4 font-bold uppercase tracking-wider text-sm"
            data-testid="contact-quote-cta"
          >
            Request a Quote
          </Link>
        </div>
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <>
      <Icon className="h-6 w-6 text-primary-brand" />
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mt-4">{label}</div>
      <div className="font-heading font-bold text-slate-900 mt-2 break-all">{value}</div>
    </>
  );
  return href ? (
    <a href={href} data-testid={testid} className="bg-brand-bg p-8 hover:bg-white transition-colors block">
      {inner}
    </a>
  ) : (
    <div data-testid={testid} className="bg-brand-bg p-8">{inner}</div>
  );
}
